import React, { useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft2,
  CloseCircle,
  InfoCircle,
  Layer,
  Location,
  SearchNormal1,
  Send2,
} from "iconsax-react";

import { CardDetailsPoi } from "../../features/map/components/CardDetailsPoi";
import { FilterCategoryModal } from "../../features/map/components/FilterCategoryModal";
import { MapLeaflet } from "../../features/map/components/MapLeaflet";
import { MapLegend } from "../../features/map/components/MapLegend";
import { MapSearchOverlay } from "../../features/map/components/MapSearchOverlay";
import {
  CATEGORY_COLORS,
  CATEGORY_LABELS,
  MAP_COPY,
  MAP_FILTER_CATEGORIES,
  NEAR_ME_FILTER,
} from "../../features/map/constants";
import { useMapClusters } from "../../features/map/hooks/useMapClusters";
import { useMapOverview } from "../../features/map/hooks/useMapOverview";
import { useMapUi } from "../../features/map/hooks/useMapUi";
import {
  filterPoisNearby,
  recordPoiAccess,
} from "../../features/map/services/mapService";
import type { MapPoiCategory } from "../../features/map/services/map.types";
import { CategoryIcon } from "../../features/map/utils/categoryIcons";
import { requestLocationPermission } from "../../features/map/utils/geolocation";
import { Md3Button } from "../../shared/components";
import { ModalShell } from "../../shared/components/ModalShell";
import { MapLayout } from "./MapLayout";
import {
  Attribution,
  BottomLeft,
  BottomSection,
  Chip,
  ChipsRow,
  Fab,
  FabGroup,
  IconBtn,
  MapRoot,
  OverlayLayer,
  SearchCard,
  SearchInput,
  SearchRow,
  SearchSubmitBtn,
  TopSection,
} from "./overviewStyles";

const MAP_SEARCH_ROW_HEIGHT = 48;
const MAP_CHIPS_ROW_HEIGHT = 40;
const MAP_TOP_GAP = 12;
const BOTTOM_CLEARANCE = 24;

const MapPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const focusPoiId = searchParams.get("poiId") ?? undefined;
  const mapUi = useMapUi();

  const {
    mapRef,
    search,
    setSearch,
    isSearchOverlayVisible,
    openSearchOverlay,
    closeSearchOverlay,
    applySearch,
    isLegendVisible,
    toggleLegend,
    activeCategory,
    isNearMeActive,
    isNearMeResolving,
    selectCategory,
    selectNearMe,
    resolveNearMeLocation,
    isCategoryModalVisible,
    closeCategoryModal,
    selectedPoi,
    setSelectedPoi,
    userCoords,
    tileStyle,
    toggleMapStyle,
    pois,
    isLocationModalVisible,
    setIsLocationModalVisible,
    focusOnUserLocation,
    initialRegion,
  } = useMapOverview({ focusPoiId });

  const searchHeaderHeight = MAP_TOP_GAP + MAP_SEARCH_ROW_HEIGHT + MAP_TOP_GAP;
  const searchToolsBottom = isSearchOverlayVisible
    ? searchHeaderHeight
    : searchHeaderHeight + MAP_CHIPS_ROW_HEIGHT;

  const visiblePois = useMemo(() => {
    const overviewPois = pois.filter((poi) =>
      MAP_FILTER_CATEGORIES.includes(poi.category)
    );
    let filtered = overviewPois;
    if (activeCategory) {
      filtered = filtered.filter((poi) => poi.category === activeCategory);
    }
    if (isNearMeActive) {
      if (userCoords) {
        filtered = filterPoisNearby(
          filtered,
          userCoords.latitude,
          userCoords.longitude
        );
      } else {
        filtered = [];
      }
    }
    return filtered;
  }, [activeCategory, isNearMeActive, pois, userCoords]);

  const clusterMode = activeCategory || isNearMeActive ? "poi" : "category";

  const {
    clusters,
    showCategoryClusterIcons,
    handleRegionChangeComplete,
    getClusterExpansionRegion,
  } = useMapClusters(visiblePois, clusterMode);

  const handleClusterPress = useCallback(
    (
      clusterId: number,
      latitude: number,
      longitude: number,
      category?: MapPoiCategory
    ) => {
      const region = getClusterExpansionRegion(
        clusterId,
        latitude,
        longitude,
        category
      );
      mapRef.current?.animateToCoordinate(
        region.latitude,
        region.longitude,
        region.latitudeDelta
      );
    },
    [getClusterExpansionRegion, mapRef]
  );

  const handleMarkerPress = (id: string) => {
    const poi = pois.find((item) => item.id === id);
    if (poi) {
      setSelectedPoi(poi);
      recordPoiAccess(id);
    }
  };

  const openPoiDetails = useCallback(
    (poiId: string, isNearbyHint?: boolean) => {
      recordPoiAccess(poiId);
      const params = new URLSearchParams();
      if (userCoords?.latitude != null) {
        params.set("lat", String(userCoords.latitude));
      }
      if (userCoords?.longitude != null) {
        params.set("lng", String(userCoords.longitude));
      }
      if (isNearbyHint) params.set("nearby", "1");
      const qs = params.toString();
      navigate(`/map/poi/${poiId}${qs ? `?${qs}` : ""}`);
    },
    [navigate, userCoords?.latitude, userCoords?.longitude]
  );

  const handleOpenDetails = () => {
    if (!selectedPoi) return;
    openPoiDetails(selectedPoi.id);
  };

  const handleOpenDetailsFromNearbyTag = () => {
    if (!selectedPoi) return;
    openPoiDetails(selectedPoi.id, true);
  };

  const handleOpenPoiDetails = useCallback(
    (poiId: string) => {
      closeSearchOverlay();
      openPoiDetails(poiId);
    },
    [closeSearchOverlay, openPoiDetails]
  );

  const handleFilterPoiSelect = useCallback(
    (poiId: string) => {
      const poi = pois.find((item) => item.id === poiId);
      if (poi) {
        setSelectedPoi(poi);
        mapRef.current?.animateToCoordinate(poi.latitude, poi.longitude, 0.05);
      }
      openPoiDetails(poiId);
    },
    [mapRef, openPoiDetails, pois, setSelectedPoi]
  );

  const renderBottomPanel = () => {
    if (selectedPoi) {
      return (
        <CardDetailsPoi
          poi={selectedPoi}
          userLatitude={userCoords?.latitude}
          userLongitude={userCoords?.longitude}
          onPress={handleOpenDetails}
          onNearbyTagPress={handleOpenDetailsFromNearbyTag}
        />
      );
    }
    if (isLegendVisible) {
      return <MapLegend />;
    }
    return null;
  };

  return (
    <MapLayout>
      <MapRoot>
        <MapLeaflet
          ref={mapRef}
          initialLatitude={initialRegion.latitude}
          initialLongitude={initialRegion.longitude}
          initialLatitudeDelta={initialRegion.latitudeDelta}
          tileStyle={tileStyle}
          clusterItems={clusters}
          clusterMode={clusterMode}
          showCategoryClusterIcons={showCategoryClusterIcons}
          selectedMarkerId={selectedPoi?.id}
          userLatitude={userCoords?.latitude}
          userLongitude={userCoords?.longitude}
          onMarkerPress={handleMarkerPress}
          onClusterPress={handleClusterPress}
          onMapStateChange={handleRegionChangeComplete}
          onMapPress={() => setSelectedPoi(null)}
        />

        <OverlayLayer>
          <TopSection $searchActive={isSearchOverlayVisible}>
            <SearchRow>
              {isSearchOverlayVisible ? (
                <IconBtn
                  type="button"
                  aria-label={MAP_COPY.back}
                  onClick={() => {
                    closeSearchOverlay();
                    setSearch("");
                    applySearch("");
                  }}
                >
                  <ArrowLeft2 size={20} />
                </IconBtn>
              ) : null}

              <SearchCard $active={isSearchOverlayVisible}>
                <SearchNormal1 size={18} />
                <SearchInput
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onFocus={openSearchOverlay}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") applySearch(search);
                  }}
                  placeholder={MAP_COPY.searchPlaceholder}
                />
                {search.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    aria-label="Limpar"
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    <CloseCircle size={18} />
                  </button>
                ) : null}
              </SearchCard>

              {isSearchOverlayVisible ? (
                <SearchSubmitBtn
                  type="button"
                  onClick={() => applySearch(search)}
                >
                  <SearchNormal1 size={18} color="#fff" variant="Bold" />
                </SearchSubmitBtn>
              ) : (
                <IconBtn
                  type="button"
                  aria-label={MAP_COPY.legendTitle}
                  onClick={toggleLegend}
                >
                  <InfoCircle size={20} />
                </IconBtn>
              )}
            </SearchRow>

            {!isSearchOverlayVisible ? (
              <ChipsRow>
                <Chip
                  type="button"
                  $active={isNearMeActive || isNearMeResolving}
                  $color={mapUi.userPin}
                  onClick={selectNearMe}
                >
                  <Send2
                    size={18}
                    variant={
                      isNearMeActive || isNearMeResolving ? "Bold" : "Outline"
                    }
                  />
                  {isNearMeResolving ? MAP_COPY.loading : MAP_COPY.nearMe}
                </Chip>

                {MAP_FILTER_CATEGORIES.map((category) => {
                  const active = activeCategory === category;
                  return (
                    <Chip
                      key={category}
                      type="button"
                      $active={active}
                      $color={CATEGORY_COLORS[category]}
                      onClick={() => selectCategory(category)}
                    >
                      <CategoryIcon
                        category={category}
                        size={20}
                        color={active ? "#fff" : CATEGORY_COLORS[category]}
                        variant={active ? "Bold" : "Outline"}
                      />
                      {CATEGORY_LABELS[category]}
                    </Chip>
                  );
                })}
              </ChipsRow>
            ) : null}
          </TopSection>

          <BottomSection>
            <BottomLeft>{renderBottomPanel()}</BottomLeft>

            <FabGroup>
              <Fab
                type="button"
                onClick={toggleMapStyle}
                aria-label="Alternar estilo"
              >
                <Layer size={22} color={mapUi.primary} variant="Bold" />
              </Fab>
              <Fab
                type="button"
                onClick={focusOnUserLocation}
                aria-label="Minha localização"
              >
                <Location size={22} color={mapUi.primary} variant="Bold" />
              </Fab>
            </FabGroup>
          </BottomSection>

          <Attribution>{MAP_COPY.osmAttribution}</Attribution>
        </OverlayLayer>

        <MapSearchOverlay
          visible={isSearchOverlayVisible}
          searchText={search}
          topOffset={searchToolsBottom}
          bottomOffset={BOTTOM_CLEARANCE}
          userLatitude={userCoords?.latitude}
          userLongitude={userCoords?.longitude}
          onSelectSearch={applySearch}
          onSelectPoi={handleOpenPoiDetails}
        />

        <FilterCategoryModal
          visible={isCategoryModalVisible}
          category={isNearMeActive ? NEAR_ME_FILTER : activeCategory}
          userLatitude={userCoords?.latitude}
          userLongitude={userCoords?.longitude}
          isResolvingLocation={isNearMeResolving}
          bottomOffset={BOTTOM_CLEARANCE}
          onClose={closeCategoryModal}
          onSelectPoi={handleFilterPoiSelect}
        />

        {isLocationModalVisible ? (
          <ModalShell
            open
            onClose={() => setIsLocationModalVisible(false)}
            aria-label={MAP_COPY.locationPermissionTitle}
          >
            <div style={{ padding: 20, maxWidth: 420 }}>
              <h2 style={{ margin: "0 0 8px" }}>
                {MAP_COPY.locationPermissionTitle}
              </h2>
              <p style={{ margin: "0 0 16px" }}>
                {MAP_COPY.locationPermissionBody}
              </p>
              <Md3Button
                title={MAP_COPY.allowLocation}
                variant="filled"
                fullWidth
                onClick={async () => {
                  const status = await requestLocationPermission();
                  setIsLocationModalVisible(false);
                  if (status === "granted") {
                    await resolveNearMeLocation();
                  }
                }}
              />
              <Md3Button
                title={MAP_COPY.cancel}
                variant="text"
                fullWidth
                onClick={() => setIsLocationModalVisible(false)}
              />
            </div>
          </ModalShell>
        ) : null}
      </MapRoot>
    </MapLayout>
  );
};

export default MapPage;
